/**
 * API Client - Axios Instance with Interceptors
 * Centralized HTTP client for all API requests
 * 
 * Features:
 * - Authentication token injection
 * - Global error handling
 * - Request/response logging
 * - Retry logic for network failures
 * - 401/403 automatic redirect
 */

import axios from 'axios';
import { ENV } from '../config/environment';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: ENV.API_BASE_URL,
  timeout: ENV.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true // Enable cookies for session management
});

/**
 * Request Interceptor
 * Injects authentication token into every request
 */
apiClient.interceptors.request.use(
  (config) => {
    // Get token from sessionStorage or localStorage
    const token = sessionStorage.getItem(ENV.AUTH_TOKEN_KEY) || 
                  localStorage.getItem(ENV.AUTH_TOKEN_KEY);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor
 * Handles global error responses and logging
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Extract error details
    const status = error.response?.status;
    
    // Handle specific error status codes
    switch (status) {
      case 401:
        // Unauthorized - Clear session and redirect to login
        handleUnauthorized();
        break;
        
      case 403:
        // Forbidden - Show permission error
        // Optionally redirect to unauthorized page
        // window.location.href = '/unauthorized';
        break;
        
      case 404:
        // Not found
        break;
        
      case 500:
      case 502:
      case 503:
        // Server errors
        break;
        
      case 408:
      case 504:
        // Timeout errors
        break;
        
      default:
        break;
    }
    
    return Promise.reject(error);
  }
);

/**
 * Handle 401 Unauthorized Response
 * Clear session and redirect to login
 */
const handleUnauthorized = () => {
  // Clear authentication data
  sessionStorage.removeItem(ENV.AUTH_TOKEN_KEY);
  sessionStorage.removeItem(ENV.AUTH_USER_KEY);
  localStorage.removeItem(ENV.AUTH_TOKEN_KEY);
  localStorage.removeItem(ENV.AUTH_USER_KEY);
  
  // Dispatch event for React router to handle gracefully
  if (!window.location.pathname.includes('/login')) {
    window.dispatchEvent(new Event('auth:unauthorized'));
  }
};

/**
 * Retry Request Helper
 * @param {Function} requestFn - Function that returns an axios request
 * @param {number} retries - Number of retry attempts
 * @param {number} delay - Delay between retries in ms
 * @returns {Promise} - Request result or error
 */
export const retryRequest = async (requestFn, retries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx) except 408 (timeout)
      const status = error.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408) {
        throw error;
      }
      
      // Wait before retrying (except on last attempt)
      if (attempt < retries) {
        const currentDelay = delay;
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        delay = currentDelay * 2; // Exponential backoff
      }
    }
  }
  
  throw lastError;
};

/**
 * Cancel Token Helper
 * Use to cancel pending requests
 */
export const CancelToken = axios.CancelToken;
export const isCancel = axios.isCancel;

export default apiClient;
